import { Component, Input, OnInit, signal } from '@angular/core';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import {
  BookingStatus,
  BookingStatusHumanized,
} from '../../../core/models/entities/booking';
import { ModalConfig } from '../../../shared/components/modal/modal.component';
import {
  ModalInstance,
  ModalService,
} from '../../../core/services/modal.service';
import { BookingsService } from 'angular/app/core/services/bookings.service';
import { ApiError } from 'angular/app/core/models/common/api-error';
import { Store } from '@ngrx/store';
import { bookingsActions } from '../../../core/store/bookings';
import { firstValueFrom, skipWhile, take } from 'rxjs';
import { fromStore } from '../../../core/store';
import { ToastService } from 'angular/app/core/services/toast.service';
import { PublicBookingEditModalComponent } from '../public-booking-edit-modal/public-booking-edit-modal.component';
import { PrivateBookingModalComponent } from '../private-booking-modal/private-booking-modal.component';
import { BookingPermission } from '../../../core/models/entities/permission';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { parseISO } from 'date-fns';
import { UpdateRecurrenceModalComponent } from '../../../shared/components/update-recurrence-modal/update-recurrence-modal.component';
import { CommunicationModalComponent } from '../../../shared/components/communication-modal/communication-modal.component';
import { CommunicationTemplateEvent } from '../../../core/models/entities/communication-template';
import { BookingsScope } from '../../../core/store/bookings/bookings.state';
import {
  selectAuthVendor,
  selectFocusedBooking,
} from '../../../core/store/reducers';

@Component({
  selector: 'app-booking-details-modal',
  template: `
    <app-modal
      [modalInstance]="modalInstance"
      (onOk)="onOk()"
      (onCancel)="onCancel()"
      >
      @if (loading()) {
        <div class="relative py-32">
          <app-loader [centered]="true" />
        </div>
      } @else {
        @if (booking()) {
          <app-booking-details
            [displayModifyButton]="
              booking()?.status === BookingStatus.Pending &&
              !booking()?.externally_managed
            "
            [displayCalendarButton]="displayCalendarButton"
            [withInvoicingDetails]="withInvoicingDetails"
            [defaultTab]="defaultTab"
            (openEditModalClicked)="openEditBookingModal()"
          ></app-booking-details>
        } @else {
          <app-empty-state [message]="error" />
        }
        @if (booking()?.externally_managed) {
          <div class="my-6 h-px bg-gray-200"></div>
          <p class="md:text-right"
            ><i class="fal fa-lock mr-3"></i> Cette réservation est gérée par un
            système externe et ne peut pas être modifiée ici.</p
            >
          }
        }
    
      </app-modal>
    `,
  standalone: false,
})
export class BookingDetailsModalComponent
  extends ModalBaseComponent
  implements OnInit
{
  @Input() bookingId: number;
  @Input() withInvoicingDetails = true;
  @Input() displayCalendarButton = false;
  @Input() defaultTab: string | null = null;
  BookingStatus = BookingStatus;
  authUser = this.store.selectSignal(fromStore.selectAuthUser);
  vendor = this.store.selectSignal(selectAuthVendor);
  error: string;
  loading = signal(false);
  booking = this.store.selectSignal(selectFocusedBooking);

  constructor(
    private modalService: ModalService,
    private bookingsService: BookingsService,
    private toastService: ToastService,
    private store: Store,
  ) {
    super();
  }

  async ngOnInit() {
    // Update config using signal.update()
    this.modalInstance.config.update((config) => ({
      ...config,
      showActionButtons: false,
      hideCloseButton: true,
      size: '2xl',
      alignTop: true,
    }));

    this.loading.set(true);
    try {
      await this.loadBooking();

      // Update config using signal.update()
      this.modalInstance.config.update((config) => ({
        ...config,
        okAction: this.okAction,
        cancelAction: this.cancelAction,
        hideCloseButton: false,
        closeOnCancel: false,
        alignTop: true,
        showActionButtons:
          (this.authUser().all_permissions.includes(BookingPermission.Update) ||
            this.booking().user_id === this.authUser().id) &&
          this.booking().status !== BookingStatus.Cancelled &&
          this.booking().status !== BookingStatus.Refused &&
          this.booking().status !== BookingStatus.PendingPayment,
      }));

      this.setTitle();
    } catch (exception) {
      this.error = (exception as ApiError).message;
    }
    this.loading.set(false);
  }

  async loadBooking() {
    const booking = (
      await firstValueFrom(this.bookingsService.view(this.bookingId))
    ).data;
    this.store.dispatch(bookingsActions.setFocusedBooking({ booking }));
  }

  setTitle() {
    this.modalInstance.config.update((config) => ({
      ...config,
      title: this.booking().company ?? this.booking().name,
      badges: [
        { variant: 'secondary', text: this.booking().reference },
        {
          variant: BookingStatusHumanized[this.booking().status].color,
          text: BookingStatusHumanized[this.booking().status].label,
        },
      ],
    }));
  }

  get okAction(): ModalConfig['okAction'] {
    let title: string;
    if (this.booking().recurrence_rule !== null && this.booking().is_public) {
      title = 'Modifier la récurrence';
    } else if (this.booking().status === BookingStatus.Confirmed) {
      title = 'Modifier';
    } else {
      title = 'Confirmer la réservation';
    }

    return {
      title: title,
      disabled: this.booking().externally_managed,
    };
  }

  get cancelAction(): ModalConfig['cancelAction'] {
    return {
      title:
        this.booking().status === BookingStatus.Confirmed
          ? 'Annuler réservation'
          : 'Refuser',
      disabled: this.booking().externally_managed,
      variant: 'outlined',
    };
  }

  openEditBookingModal() {
    if (!this.booking().is_public) {
      const instance = this.modalService.open(PrivateBookingModalComponent, {
        size: 'screen',
        title: 'Modifier réservation',
      });
      (instance.componentRef.instance as PrivateBookingModalComponent).booking =
        this.booking();
      (
        instance.componentRef.instance as PrivateBookingModalComponent
      ).bookingSaved.subscribe(() => {
        this.store.dispatch(
          bookingsActions.findAll({ scope: BookingsScope.All }),
        );

        this.store
          .select(fromStore.selectAuthUser)
          .pipe(take(1))
          .subscribe((user) => {
            if (user?.id === this.booking().user_id) {
              this.store.dispatch(
                bookingsActions.findAll({ scope: BookingsScope.Personal }),
              );
            }
          });
        this.setTitle();
      });
    } else {
      const instance = this.modalService.open(PublicBookingEditModalComponent, {
        size: 'screen',
        title: 'Modifier réservation',
      });
      (
        instance.componentRef.instance as PublicBookingEditModalComponent
      ).bookingSaved.subscribe(() => {
        if (this.booking().user_id) {
          this.store
            .select(fromStore.selectAuthUser)
            .pipe(take(1))
            .subscribe((user) => {
              if (this.booking().user_id === user?.id) {
                this.store.dispatch(
                  bookingsActions.findAll({
                    scope: BookingsScope.Personal,
                  }),
                );
              }
            });
        }
        this.store.dispatch(
          bookingsActions.findAll({ scope: BookingsScope.All }),
        );
        if (this.booking().status === BookingStatus.Pending) {
          this.store.dispatch(bookingsActions.findAllPending());
        }
        this.setTitle();
      });
    }
  }

  onOk() {
    if (this.booking().status === BookingStatus.Pending) {
      this.openCommunicationModalWithBookingStatus(BookingStatus.Confirmed);
    } else {
      this.openEditBookingModal();
    }
  }

  onCancel() {
    if (this.booking().status === BookingStatus.Pending) {
      this.openCommunicationModalWithBookingStatus(BookingStatus.Refused);
    } else {
      this.openCommunicationModalWithBookingStatus(BookingStatus.Cancelled);
    }
  }

  getConfirmModalTitle(status: BookingStatus) {
    switch (status) {
      case BookingStatus.Cancelled:
        return 'Annuler la réservation';
      case BookingStatus.Refused:
        return 'Refuser la réservation';
      case BookingStatus.Confirmed:
        return 'Confirmer la réservation';
    }

    return '';
  }

  async openCommunicationModalWithBookingStatus(status: BookingStatus) {
    const instance = this.modalService.open(CommunicationModalComponent, {
      title: this.getConfirmModalTitle(status),
      size: 'xl',
      okAction: {
        title: status === BookingStatus.Confirmed ? 'Valider' : 'Envoyer',
      },
    });
    const communicationModal = instance.componentRef
      .instance as CommunicationModalComponent;
    communicationModal.communicationType = ('BOOKING_' +
      status) as CommunicationTemplateEvent;
    communicationModal.onFormSave.subscribe(
      async (data: {
        message: string;
        confirm_without_payment?: boolean;
        send_confirmation_email: boolean;
      }) => {
        this.updateBooking(data, status, instance);
      },
    );
  }

  async updateBooking(
    data: {
      message: string;
      confirm_without_payment?: boolean;
      send_confirmation_email: boolean;
    },
    status: BookingStatus,
    communicationModalInstance: ModalInstance,
  ) {
    try {
      let updateRecurrenceType = null;

      if (this.booking()?.recurrence && status === BookingStatus.Cancelled) {
        const instance = this.modalService.open(
          UpdateRecurrenceModalComponent,
          {
            title: 'Annuler réservation',
            okAction: {
              title: "Valider l'annulation",
            },
          },
        );
        const component = instance.componentRef
          .instance as UpdateRecurrenceModalComponent;
        component.originalBooking = this.booking();
        component.updatedBooking = this.booking();
        const value = await firstValueFrom(component.onClosed.pipe(take(1)));
        if (!value) return;
        this.modalService.close();
        updateRecurrenceType = value;
      }

      communicationModalInstance.saving = true;
      await this.bookingsService.updateStatus(this.booking().id, {
        id: this.booking().id,
        status,
        start: DatetimeHelper.dateToApiFormat(
          typeof this.booking().start === 'string'
            ? parseISO(this.booking().start)
            : (this.booking().start as any),
          true,
        ),
        update_recurrence_type: updateRecurrenceType,
        update_comment: data.message,
        confirm_without_payment: data.confirm_without_payment,
        send_confirmation_email: data.send_confirmation_email,
      });

      this.store.dispatch(
        bookingsActions.findAll({ scope: BookingsScope.All }),
      );
      this.store
        .select(fromStore.selectAuthUser)
        .pipe(take(1))
        .subscribe((user) => {
          if (user?.id === this.booking().user_id) {
            this.store.dispatch(
              bookingsActions.findAll({ scope: BookingsScope.Personal }),
            );
          }
        });
      if ([BookingStatus.Confirmed, BookingStatus.Refused].includes(status)) {
        this.store.dispatch(bookingsActions.findAllPending());
      }
      switch (status) {
        case BookingStatus.Cancelled:
          this.toastService.success('Réservation annulée.');
          break;
        case BookingStatus.Confirmed:
          this.toastService.success('Réservation confirmée.');
          break;
        case BookingStatus.Refused:
          this.toastService.success('Réservation refusée.');
          break;
      }

      if ([BookingStatus.Confirmed, BookingStatus.Pending].includes(status)) {
        this.store
          .select(fromStore.selectAllPendingBookingsLoading)
          .pipe(
            skipWhile((value) => value),
            take(1),
          )
          .subscribe(() => {
            communicationModalInstance.saving = false;
            this.modalService.closeAllModals();
          });
      } else {
        communicationModalInstance.saving = false;
        this.modalService.closeAllModals();
      }
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
      communicationModalInstance.saving = false;
      // En cas d'erreur de débit, on recharge les données pour mettre à jour le message
      await this.loadBooking();
    }
  }
}
